/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {ResponseData, showDialog, stopPropagation} from '@dvrd/dvr-controls';
import EmailStep from "../../../models/emailStepModel";
import {AppContext} from "../../misc/appContext";
import Attachment from "../../../models/attachmentModel";
import {createErrorMessage} from "../../../util/utils";
import {onShowConfirmSnackbar} from "../../../util/renderUtil";
import AttachmentSelect from "./attachmentSelect";
import {ActivateHandle} from "../../../util/interfaces";

interface Props {
    onReplaceStep: (step: EmailStep) => void;
}

const SEARCH_TIMEOUT = 300;

function AttachmentSelectController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const context = useContext(AppContext);
    const {onReplaceStep} = props;
    const [step, setStep] = useState(new EmailStep());
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [attachments, setAttachments] = useState<Array<Attachment>>([]);
    const [search, setSearch] = useState('');
    const allAttachments = useRef<Array<Attachment>>([]);
    const searchTimeout = useRef<number | null>(null);
    const hasChanged = useRef(false);

    function onActivate(step: EmailStep) {
        setStep(step);
        setActive(true);
        setLoading(true);
        loadAttachments({step});
    }

    function onSelectAttachment(attachment: Attachment) {
        return function (selected: boolean) {
            hasChanged.current = true;
            const _step = step.clone();
            if (selected) _step.addAttachment(attachment);
            else _step.removeAttachment(attachment);
            setStep(_step);
            filterAttachments(_step);
        }
    }

    function onDelete(attachment: Attachment) {
        return function (evt: React.MouseEvent) {
            stopPropagation(evt);
            showDialog('Weet je zeker dat je deze bijlage wilt verwijderen? Deze wordt dan ook niet meer meegestuurd ' +
                'met ingestelde e-mails.', 'Bevestig', ['Nee', {
                label: 'Ja',
                primary: false,
                onClick: onConfirmDelete(attachment)
            }]);
        }
    }

    function onConfirmDelete(attachment: Attachment) {
        return function () {
            setLoading(true);
            attachment.delete((attachment: Attachment, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    allAttachments.current = allAttachments.current.filter((att: Attachment) =>
                        att.id !== attachment.id);
                    filterAttachments();
                    onShowConfirmSnackbar('De bijlage is verwijderd');
                } else showDialog(createErrorMessage(data.message ?? 'Het verwijderen van de bijlage is niet gelukt.'),
                    'Verwijderen mislukt');
            });
        }
    }

    function onSubmit() {
        if (!hasChanged.current) return onClose();
        setLoading(true);
        step.update((step: EmailStep, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                hasChanged.current = false;
                onReplaceStep(step);
                onShowConfirmSnackbar('De e-mail is bijgewerkt');
                onClose();
            } else showDialog(createErrorMessage(data.message ?? 'Het bijwerken van de e-mail is niet gelukt.'),
                'Bijwerken mislukt');
        })
    }

    function onClose() {
        setActive(false);
        setLoading(false);
        setStep(new EmailStep());
        setAttachments([]);
        allAttachments.current = [];
    }

    function onAddAttachments(attachments: Attachment[]) {
        setLoading(false);
        allAttachments.current = allAttachments.current.concat(attachments);
        filterAttachments();
    }

    function onSearch(value: string) {
        clearSearchTimeout();
        setSearch(value);
        searchTimeout.current = window.setTimeout(() => {
            loadAttachments({search: value});
        }, SEARCH_TIMEOUT);
    }

    function onStartUpload() {
        setLoading(true);
    }

    function onClickWithInvoice() {
        hasChanged.current = true;
        const _step = step.clone();
        _step.withInvoice = true;
        setStep(_step);
    }

    function onDeselectInvoice() {
        hasChanged.current = true;
        const _step = step.clone();
        _step.withInvoice = false;
        setStep(_step);
    }

    function clearSearchTimeout() {
        if (searchTimeout.current === null) return;
        window.clearTimeout(searchTimeout.current);
        searchTimeout.current = null;
    }

    function loadAttachments(params?: { search?: string; step?: EmailStep }) {
        const {account} = context.accountContext;
        if (!account) return;
        if (!loading) setLoading(true);
        const _search = params?.search ?? search;
        Attachment.getAll(account.id, -1, -1, (attachments: Attachment[], success: boolean, data: ResponseData) => {
            setLoading(false);
            if (data.success) {
                allAttachments.current = attachments.slice();
                filterAttachments(params?.step);
            } else
                showDialog(createErrorMessage(data.message ?? 'Het laden van de bijlages is niet gelukt.'), 'Laden mislukt');
        }, _search);
    }

    function filterAttachments(useStep?: EmailStep) {
        const all = allAttachments.current;
        const _step = useStep ?? step;
        const attachments = all.filter((attachment: Attachment) => !_step.hasAttachment(attachment));
        setAttachments(attachments);
    }

    useImperativeHandle(ref, () => ({
        activate: onActivate
    }));

    useEffect(() => {
        return function () {
            clearSearchTimeout();
        }
    }, []);

    return (
        <AttachmentSelect onSelect={onSelectAttachment} onClose={onClose} onSubmit={onSubmit}
                          onAddAttachments={onAddAttachments} onSearch={onSearch} onStartUpload={onStartUpload}
                          onDelete={onDelete} onClickWithInvoice={onClickWithInvoice}
                          onDeselectInvoice={onDeselectInvoice} attachments={attachments}
                          selectedAttachments={step.attachments} searchValue={search} loading={loading} active={active}
                          withInvoice={step.withInvoice}/>
    );
}

export default forwardRef<ActivateHandle, Props>(AttachmentSelectController);